import { Entity } from '@nx-smartmonkey/shared/domain';
import { Driver } from '../driver/Driver';
import { GeoFence } from '../geoFence/GeoFence';
import { Stop } from '../stop/Stop';
import { PlanStatus } from './PlanStatus';

export interface PlanProps {
  organization_id: string;
  project_id: string;
  status: PlanStatus;
  execution_date: Date;
  label?: string;
  drivers?: Driver[];
  stops?: Stop[];
  geo_fences: GeoFence[];
  completed_stops?: number;
  canceled_stops?: number;
  pending_stops?: number;
  incomplete_stops?: number;
  total_stops?: number;
  total_drivers?: number;
  created_by?: string;
  created_at?: Date;
  updated_at?: Date;
  deleted_at?: Date;
  deleted?: boolean;
}

export class Plan extends Entity<PlanProps> {
  private driversMap: { [driverstring: string]: Driver } = {};
  private stopsMap: { [stopstring: string]: Stop } = {};

  get id(): string {
    return this._id!;
  }
  get organization_id(): string {
    return this.props.organization_id;
  }
  get project_id(): string {
    return this.props.project_id;
  }
  get status(): PlanStatus {
    return this.props.status;
  }
  get execution_date(): Date {
    return this.props.execution_date;
  }
  get label(): string | undefined {
    return this.props.label;
  }
  get drivers(): Driver[] {
    return this.props.drivers || [];
  }
  set drivers(drivers: Driver[]) {
    this.props.drivers = drivers;
  }
  get geo_fences(): GeoFence[] {
    return this.props.geo_fences;
  }
  set geo_fences(geo_fences: GeoFence[]) {
    this.props.geo_fences = geo_fences;
  }
  get stops(): Stop[] {
    return this.props.stops || [];
  }
  set stops(stops: Stop[]) {
    this.props.stops = stops;
  }
  get completed_stops(): number {
    return this.props.completed_stops ?? 0;
  }
  set completed_stops(completed_stops: number) {
    this.props.completed_stops = completed_stops;
  }
  get canceled_stops(): number {
    return this.props.canceled_stops ?? 0;
  }
  set canceled_stops(canceled_stops: number) {
    this.props.canceled_stops = canceled_stops;
  }
  get pending_stops(): number {
    return this.props.pending_stops ?? 0;
  }
  set pending_stops(pending_stops: number) {
    this.props.pending_stops = pending_stops;
  }
  get total_stops(): number {
    return this.props.total_stops ?? 0;
  }
  set total_stops(total_stops: number) {
    this.props.total_stops = total_stops;
  }
  get total_drivers(): number {
    return this.props.total_drivers || 0;
  }
  set total_drivers(total_drivers: number) {
    this.props.total_drivers = total_drivers;
  }
  get incomplete_stops(): number {
    return this.props.incomplete_stops || 0;
  }
  set incomplete_stops(incomplete_stops: number) {
    this.props.incomplete_stops = incomplete_stops;
  }
  get created_by(): string | undefined {
    return this.props.created_by;
  }
  get created_at(): Date | undefined {
    return this.props.created_at;
  }
  set created_at(created_at: Date | undefined) {
    this.props.created_at = created_at;
  }
  get updated_at(): Date | undefined {
    return this.props.updated_at;
  }
  get deleted_at(): Date | undefined {
    return this.props.deleted_at;
  }
  get deleted(): boolean {
    return this.props.deleted ?? false;
  }

  private constructor(props: PlanProps, id?: string) {
    super(props, id);
    props.drivers?.forEach((driver) => {
      this.driversMap[driver.id] = driver;
    });
    props.stops?.forEach((stop) => {
      this.stopsMap[stop.id] = stop;
    });
  }

  getStop(id: string) {
    return this.stopsMap[id];
  }
  getDriver(id: string) {
    return this.driversMap[id];
  }

  deleteGeoFence(geoFenceId: string) {
    this.geo_fences = this.geo_fences.filter((g) => g.id !== geoFenceId);
  }

  static create(props: PlanProps, id?: string) {
    return new Plan(props, id);
  }
}
